import React from 'react';
import { motion } from 'framer-motion';
import Header from '@/components/Header.jsx';
import Footer from '@/components/Footer.jsx';
import HoverSlideCard from '@/components/HoverSlideCard.jsx';
import Seo from '@/components/Seo.jsx';
function AboutPage() {
  return <>
      <Seo
        title="À propos - Neuvième Continent"
        description="Découvrez ce qu'est le jeu de rôle (JDR) et notre projet associatif à Nantes."
      />

      <div className="min-h-screen bg-background flex flex-col">
        <Header />

        <main className="flex-grow py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          {/* Background decorative elements */}
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
            <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-[#7A4B9E]/10 blur-[120px]" />
            <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-[#E8706A]/10 blur-[120px]" />
          </div>

          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="text-center mb-16">
              <h1 className="font-pixel text-5xl md:text-6xl text-foreground mb-6 tracking-wide drop-shadow-lg">
                À Propos de Nous
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto font-medium">
                Notre association est une structure loi 1901 à but non lucratif, ancrée dans le territoire Nantais et de son agglomération.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start justify-center">
              
              {/* CARD 1: Qu'est-ce que le JDR ? */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }}>
                <HoverSlideCard title="Qu'est-ce que le JDR ?" imageSrc="/jdr.jpg">
                  <p>
                    Le jeu de rôle prend de nombreuses formes : grandeur nature, murder party, jeu vidéo… C'est sa déclinaison sur table  qui nous passionne ici, et c'est elle que nous vous proposons d'explorer.
                  </p>
                  
                  <h3 className="text-[#E8706A] font-bold mt-4 mb-2">Le principe</h3>
                  <p>
                    Imaginez quelques personnes réunies autour d'une table, construisant ensemble une histoire en la racontant à voix haute. Ni plateau, ni écran : tout se joue dans les mots et l'imaginaire partagé.
                  </p>
                  <p>
                    Chaque participant·e incarne un personnage fictif et prend des décisions en son nom — quoi faire, quoi dire, comment réagir. Ces choix font avancer le récit. Une personne occupe un rôle particulier : le·la conteur.euse qui  pose le décor, anime l'univers et ses habitant·es, et fait rebondir l'histoire au fil des actions du groupe. 
                  </p>
                  <p>
                    Pour résoudre les moments d'incertitude — une attaque risquée, une négociation tendue — on fait appel aux dés. Leur résultat injecte une dose d'inattendu qui enrichit le récit et ménage des surprises, y compris pour le·la conteur.euse.
                  </p>

                  <h3 className="text-[#E8706A] font-bold mt-4 mb-2">Aucun prérequis nécessaire</h3>
                  <p>
                    Inutile d'être comédien·ne, orateur·rice ou expert·e en règles. Il n'est pas question de jouer au sens théâtral : chacun·e s'implique à sa façon, selon son aisance et son envie. Ce qui compte avant tout, c'est l'envie de partager un moment et d'y contribuer à sa mesure.
                  </p>
                  <p>
                    Le JDR s'aventure dans tous les genres — médiéval-fantastique, polar, science-fiction, horreur, comédie — et convient aussi bien aux personnes qui découvrent l'activité qu'à celles qui la pratiquent depuis des années.
                  </p>

                  <h3 className="text-[#E8706A] font-bold mt-4 mb-2">Un jeu collectif avant tout</h3>
                  <p>
                    Ce qui rend le JDR unique parmi bien des loisirs, c'est sa dimension profondément collective. Il n'existe ni vainqueur·e ni perdant·e : le groupe avance et surmonte les obstacles ensemble, et c'est précisément là que se niche sa valeur. Écouter, chercher des solutions communes, faire de la place à tou·tes.
                  </p>
                  <p>
                    C'est pourquoi nous y voyons bien plus qu'un divertissement — c'est un espace de rencontre, d'expression et de complicité.
                  </p>
                </HoverSlideCard>
              </motion.div>

              {/* CARD 2: Notre projet associatif */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.4
            }}>
                <HoverSlideCard title="Notre projet associatif" imageSrc="https://images.unsplash.com/photo-1678132218412-0f18fab9b537?q=80&w=2000&auto=format&fit=crop">
                  <p>
                    Il est né d'une conviction simple : le jeu de rôle sur table est une activité riche, accessible à toutes et à tous, et mérite une place de choix dans la vie culturelle et ludique locale.
                  </p>

                  <h3 className="text-[#E8706A] font-bold mt-4 mb-2">Jouer ensemble, pour tout le monde</h3>
                  <p>
                    Le JDR est un art de l'histoire collective — un mélange d'improvisation, de narration à plusieurs voix et d'imagination en mouvement, qui ne nécessite ni matériel onéreux ni savoir-faire préalable pour être pleinement apprécié.
                  </p>
                  <p>
                    Notre première ambition est de faire découvrir cette pratique au plus grand nombre. Curieux·ses néophytes, joueur·euses occasionnel·les ou passionné·es aguerri·es : il y a une place pour chacun·e autour de nos tables. Nous accordons une attention toute particulière à l'accueil des personnes qui découvrent le JDR, en leur proposant un environnement bienveillant et des initiations conçues pour les mettre à l'aise dès la première séance.
                  </p>

                  <h3 className="text-[#E8706A] font-bold mt-4 mb-2">Une ambiance conviviale au cœur de notre pratique</h3>
                  <p>
                    Ce qui donne sa saveur à une session de jeu de rôle, ce n'est pas d'abord le scénario ou la maîtrise des règles : c'est l'énergie qui circule entre les participant·es. Nous organisons des séances régulières dans un esprit résolument détendu, où le plaisir et la bonne humeur l'emportent sur toute forme de performance.
                  </p>
                  <p>
                    Nous portons des valeurs de respect, de liberté d'expression et d'inclusion. Quel que soit son parcours, son âge ou son niveau d'expérience, chaque membre doit pouvoir jouer dans les mêmes conditions que les autres. Cette exigence d'égalité d'accès est fondamentale pour nous — elle guide notre fonctionnement au quotidien et structure notre engagement associatif.
                  </p>

                  <h3 className="text-[#E8706A] font-bold mt-4 mb-2">S'engager dans la vie culturelle nantaise</h3>
                  <p>
                    Le jeu de rôle est une forme d'expression culturelle à part entière, et nous souhaitons contribuer à sa reconnaissance sur notre territoire. Jeune association en cours de construction, nous avons pour ambition de tisser progressivement des liens avec le tissu associatif et culturel de Nantes et de son agglomération : présence aux festivals et conventions ludiques, participation aux événements locaux, et développement de partenariats avec les structures éducatives et culturelles de la région.
                  </p>
                  <p>
                    Ces premiers jalons posés, nous espérons faire du jeu de rôle sur table une activité toujours plus visible, reconnue et accessible — pour que chacun·e puisse, à son tour, pousser la porte de l'imaginaire.
                  </p>
                </HoverSlideCard>
              </motion.div>

            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>;
}
export default AboutPage;